'use client'

import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react'
import clsx from 'clsx'
import InputLabel from 'lib/components/Form/InputLabel/InputLabel'
import Icon from 'lib/components/Icon/Icon'
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'

export type RenderChildSummary = (
  value?: string | number,
  fieldName?: string,
) => string
export interface GroupedInputProps {
  /**
   * Optional label text
   */
  label?: string
  /**
   * Optional placeholder text to show in button when summary is empty
   */
  placeholder?: string
  /**
   * Optional children. Can be ReactNode, or a render function
   * - `<ReactNode>` : Will be rendered as children in a normal fashion
   * - `<Function>` : Should return ReactNode, Fragment, or an array of ReactNodes.
   *               Passes a `registerChild` function argument, which can be used to register an input in GroupedInput's context
   */
  children?:
    | ReactNode
    | ((
        registerChild: (
          fieldName: string,
          renderSummary?: RenderChildSummary,
        ) => Record<string | 'name', any>,
        ...props: any
      ) => ReactNode)
  /**
   * A function that allows for customized rendering of a registered input's summary.
   * Passes the input's `value` and `name` when called
   */
  renderChildSummary?: RenderChildSummary
  /**
   * A function that allows for customized rendering of the group summary.
   * Passes an array of all registered inputs' summaries
   */
  renderSummary?: (childrenSummaries: string[]) => string
}

export default function GroupedInput({
  label,
  placeholder = '',
  children,
  renderChildSummary,
  renderSummary,
}: GroupedInputProps) {
  /**
   * Locally stores individual summary of each input child
   * Maps input name to it's summary
   */
  const summariesRef = useRef<Record<string, string>>({})
  /**
   * Locally stores values for each input
   * Headless UI's Popover unmounts its children when closed, so the inputs are reset
   * We're storing these input values so we can pass them back to the child inputs on remount
   */
  const valuesRef = useRef<Record<string, string | number | undefined>>({})
  /**
   * Group summary
   */
  const [summary, setSummary] = useState('')

  const updateSummary = useCallback(() => {
    const childSummaries = Object.values(summariesRef.current).filter(Boolean)
    const summary = renderSummary
      ? renderSummary(childSummaries)
      : childSummaries.join(', ')

    setSummary(summary)
  }, [summariesRef, renderSummary])

  const inputChangeFactory = useCallback(
    (renderInputSummary?: RenderChildSummary) =>
      (value?: string | number, fieldName?: string) => {
        if (!fieldName) {
          return
        }

        // Store value
        valuesRef.current[fieldName] = value

        // Store summary for this child
        const childSummaryRenderer =
          renderInputSummary || renderChildSummary || undefined
        summariesRef.current[fieldName] =
          value && childSummaryRenderer
            ? childSummaryRenderer(value, fieldName)
            : ''

        // Update summary
        updateSummary()
      },
    [valuesRef, renderChildSummary, updateSummary],
  )

  const registerChild = useCallback(
    (name: string, renderSummary?: RenderChildSummary) => {
      valuesRef.current[name] = ''
      summariesRef.current[name] = ''

      return {
        name,
        onChange: inputChangeFactory(renderSummary),
      }
    },
    [inputChangeFactory, valuesRef, summariesRef],
  )

  const renderedChildren = useMemo(
    () => (typeof children === 'function' ? children(registerChild) : children),
    [children, registerChild],
  )

  return (
    <div>
      <InputLabel>{label}</InputLabel>
      <Popover className="group">
        {({ open }) => (
          <>
            <PopoverButton
              className={clsx(
                'flex justify-between items-center rounded-8 border border-neutral-500 mt-2 w-[410px] h-12 px-4 text-700 bg-white type-body-8 whitespace-nowrap lg:h-14',
                // Open state
                'group-data[open]:border-neutral-700',
                // Hover state
                'focus:outline-none focus-outline:outline-none focus:border-neutral-700',
              )}
            >
              <span className="overflow-ellipsis overflow-hidden">
                {summary || placeholder}
              </span>
              <Icon
                icon="chevron-down"
                className="w-6 h-6 ml-auto group-data-[open]:rotate-180 flex-shrink-0"
              />
            </PopoverButton>
            <Transition
              show={open}
              unmount={false}
              enter="duration-200 ease-out"
              enterFrom="opacity-0 -translate-y-2"
              enterTo="opacity-100 translate-0"
              leave="ease-out"
              leaveFrom="opacity-100 translate-0"
              leaveTo="opacity-0 -translate-y-2"
            >
              <PopoverPanel
                static
                modal
                focus
                anchor={{ to: 'bottom start' }}
                className="w-[--button-width] shadow-popover rounded-8 mt-2"
              >
                <div className="w-full bg-white grid grid-cols-2 p-4 gap-2">
                  {renderedChildren}
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  )
}
